import React from 'react';
import { useNavigate } from 'react-router-dom';

const CookiePolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          <div className="navbar-actions">
            <button 
              onClick={() => navigate('/')}
              className="btn btn-ghost"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/signin')}
              className="btn btn-ghost"
            >
              Sign in
            </button>
            <button 
              onClick={() => navigate('/signup')}
              className="btn btn-primary"
            >
              Sign up
            </button>
          </div>
        </div>
      </nav>

      {/* Cookie Policy Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Cookie Policy</h1>
          <p className="text-lg text-gray-600">
            Last Updated: November 2024
          </p>
          <div className="mt-2 text-sm text-gray-500">
            Learn how we use cookies and similar technologies on NexSync
          </div>
        </div>

        {/* Cookie Consent Banner Preview */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 font-bold">🍪</span>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Cookie Consent</h3>
              <p className="text-blue-700 mb-4">
                When you first visit our website, you'll see a cookie consent banner that allows 
                you to choose which types of cookies you accept. You can change your preferences 
                at any time using the button below.
              </p>
              <div className="flex flex-wrap gap-3">
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Accept All Cookies
                </button>
                <button className="px-4 py-2 bg-white text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50">
                  Customize Settings
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                  Reject Non-Essential
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Policy Content */}
        <div className="bg-white rounded-xl shadow-md p-8 space-y-12">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                Cookies are small text files that are placed on your computer or mobile device 
                when you visit a website. They are widely used to make websites work more 
                efficiently and provide information to the website owners.
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">How Cookies Work</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-blue-600">📁</span>
                    </div>
                    <h4 className="font-medium text-gray-900">Storage</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      Small files stored on your device
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-green-600">🔗</span>
                    </div>
                    <h4 className="font-medium text-gray-900">Identification</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      Help identify your device and preferences
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-purple-600">⚙️</span>
                    </div>
                    <h4 className="font-medium text-gray-900">Functionality</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      Enable website features and personalization
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Cookie Types */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Types of Cookies We Use</h2>
            <div className="space-y-6">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-4 py-3 text-left">Type</th>
                      <th className="border border-gray-200 px-4 py-3 text-left">Purpose</th>
                      <th className="border border-gray-200 px-4 py-3 text-left">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-4 py-3 font-medium">
                        <span className="inline-block w-3 h-3 bg-blue-600 rounded-full mr-2"></span>
                        Essential
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        Required for basic website functionality
                      </td>
                      <td className="border border-gray-200 px-4 py-3">Session</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-200 px-4 py-3 font-medium">
                        <span className="inline-block w-3 h-3 bg-green-600 rounded-full mr-2"></span>
                        Functional
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        Remember preferences and settings
                      </td>
                      <td className="border border-gray-200 px-4 py-3">30 days</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-4 py-3 font-medium">
                        <span className="inline-block w-3 h-3 bg-yellow-600 rounded-full mr-2"></span>
                        Analytics
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        Track website usage and performance
                      </td>
                      <td className="border border-gray-200 px-4 py-3">1 year</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-200 px-4 py-3 font-medium">
                        <span className="inline-block w-3 h-3 bg-purple-600 rounded-full mr-2"></span>
                        Marketing
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        Personalize ads and marketing content
                      </td>
                      <td className="border border-gray-200 px-4 py-3">6 months</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-blue-600">✓</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Essential Cookies</h3>
                  </div>
                  <p className="text-gray-700">
                    These cookies are necessary for the website to function and cannot be 
                    switched off. They are usually only set in response to actions made by 
                    you such as logging in or filling in forms.
                  </p>
                  <div className="mt-4 text-sm text-gray-600">
                    <strong>Examples:</strong> Session cookies, security cookies
                  </div>
                </div>

                <div className="bg-green-50 p-6 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <span className="text-green-600">⚙️</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Functional Cookies</h3>
                  </div>
                  <p className="text-gray-700">
                    These cookies enable the website to provide enhanced functionality and 
                    personalization. They may be set by us or by third-party providers whose 
                    services we have added to our pages.
                  </p>
                  <div className="mt-4 text-sm text-gray-600">
                    <strong>Examples:</strong> Language preferences, region settings
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Specific Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Specific Cookies We Use</h2>
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">First-Party Cookies</h3>
                <div className="space-y-4">
                  <div className="border-b border-gray-200 pb-4">
                    <h4 className="font-medium text-gray-900">nexsync_session</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      <strong>Purpose:</strong> Maintains user session state across page requests
                    </p>
                    <p className="text-gray-600 text-sm">
                      <strong>Duration:</strong> Session
                    </p>
                  </div>
                  
                  <div className="border-b border-gray-200 pb-4">
                    <h4 className="font-medium text-gray-900">nexsync_preferences</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      <strong>Purpose:</strong> Stores user preferences and settings
                    </p>
                    <p className="text-gray-600 text-sm">
                      <strong>Duration:</strong> 30 days
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900">nexsync_analytics</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      <strong>Purpose:</strong> Tracks user behavior for analytics
                    </p>
                    <p className="text-gray-600 text-sm">
                      <strong>Duration:</strong> 1 year
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Third-Party Cookies</h3>
                <p className="text-gray-700 mb-4">
                  We also use cookies from trusted third-party services to enhance our platform 
                  functionality:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Google Analytics</h4>
                    <p className="text-gray-600 text-sm">
                      Helps us understand how users interact with our website
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Stripe</h4>
                    <p className="text-gray-600 text-sm">
                      Payment processing and fraud prevention
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Cookie Management */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Managing Cookies</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Browser Controls</h3>
                <p className="text-gray-700 mb-4">
                  Most web browsers allow you to control cookies through their settings. 
                  Here's how to manage cookies in popular browsers:
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Google Chrome</h4>
                    <p className="text-gray-600 text-sm">
                      Settings → Privacy and Security → Cookies and other site data
                    </p>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Mozilla Firefox</h4>
                    <p className="text-gray-600 text-sm">
                      Options → Privacy & Security → Cookies and Site Data
                    </p>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Safari</h4>
                    <p className="text-gray-600 text-sm">
                      Preferences → Privacy → Cookies and website data
                    </p>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Microsoft Edge</h4>
                    <p className="text-gray-600 text-sm">
                      Settings → Cookies and site permissions → Cookies and site data
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">Important Note</h3>
                <p className="text-yellow-700">
                  Disabling cookies may affect the functionality of our website and limit 
                  your ability to use certain features. Essential cookies cannot be disabled 
                  as they are required for the platform to function properly.
                </p>
              </div>
            </div>
          </section>

          {/* Do Not Track */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Do Not Track Signals</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                Some browsers have a "Do Not Track" feature that lets you tell websites 
                that you do not want to have your online activities tracked. Currently, 
                our systems do not recognize browser "Do Not Track" signals.
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Our Position</h3>
                <p className="text-gray-700">
                  We honor users' preferences through our cookie consent manager rather than 
                  through browser-based "Do Not Track" signals. You can control your tracking 
                  preferences through our cookie settings panel.
                </p>
              </div>
            </div>
          </section>

          {/* Updates */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Updates to This Policy</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                We may update this Cookie Policy from time to time to reflect changes in 
                our practices or for other operational, legal, or regulatory reasons.
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Notification</h3>
                <p className="text-gray-700">
                  When we make material changes to this policy, we will notify you by 
                  displaying a prominent notice on our website or by sending you an 
                  email notification.
                </p>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Contact Us</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <div className="space-y-2">
                <p className="font-medium text-gray-900">Email: privacy@nexsync.com</p>
                <p className="font-medium text-gray-900">Phone: +234 800 123 4567</p>
                <p className="font-medium text-gray-900">
                  Address: 123 Innovation Drive, Victoria Island, Lagos, Nigeria
                </p>
              </div>
            </div>
          </section>

          {/* Cookie Settings */}
          <div className="border-t border-gray-200 pt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Cookie Settings</h3>
                <p className="text-gray-600">
                  You can update your cookie preferences at any time using the button below.
                </p>
              </div>
              <button className="btn btn-primary">
                Update Cookie Preferences
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between mt-8">
          <button 
            onClick={() => navigate('/termsofservice')}
            className="btn btn-secondary"
          >
            ← Terms of Service
          </button>
          <button 
            onClick={() => navigate('/privacypolicy')}
            className="btn btn-primary"
          >
            Privacy Policy →
          </button>
        </div>

        {/* Quick Reference */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Quick Cookie Reference</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-600 text-2xl">🔐</span>
              </div>
              <h4 className="font-semibold text-gray-900">Essential</h4>
              <p className="text-sm text-gray-600 mt-1">Required</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-green-600 text-2xl">⚙️</span>
              </div>
              <h4 className="font-semibold text-gray-900">Functional</h4>
              <p className="text-sm text-gray-600 mt-1">Optional</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-yellow-600 text-2xl">📊</span>
              </div>
              <h4 className="font-semibold text-gray-900">Analytics</h4>
              <p className="text-sm text-gray-600 mt-1">Optional</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-purple-600 text-2xl">🎯</span>
              </div>
              <h4 className="font-semibold text-gray-900">Marketing</h4>
              <p className="text-sm text-gray-600 mt-1">Optional</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="navbar-brand">
                <div className="brand-logo">NS</div>
                <span className="brand-text">NexSync</span>
              </div>
              <p className="footer-description">
                We value your privacy and choice.
              </p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4>Legal</h4>
                <button onClick={() => navigate('/privacypolicy')} className="footer-link">Privacy Policy</button>
                <button onClick={() => navigate('/termsofservice')} className="footer-link">Terms of Service</button>
                <button onClick={() => navigate('/cookiepolicy')} className="footer-link">Cookie Policy</button>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} NexSync. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicy;